import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getPatient } from "../../services/consultation.services";
import { CommonState } from "../../types/createUserForm";
import { getCookie } from "../../utils/cookies/cookieUtils";

const parseBranchCookie = (cookieName: string) => {
  try {
    const v = getCookie(cookieName);
    return v ? JSON.parse(v as string) : null;
  } catch (err) {
    return null;
  }
};

const initialBranch = parseBranchCookie("branch");

const initialState: CommonState = {
  profile: {
    name: "",
    uuid: "",
    age: "",
    sex: "",
    contact_number: "",
    email_id: "",
    birthday: "",
    caseId: "",
  },
  loading: false,
  error: null,
  errors: {},
  paginationModel: {
    pageSize: 10,
    page: 1,
    totalRecords: 0,
    currentPage: 0,
    nextPage: null,
    previousPage: null,
    totalPages: 0,
  },
  isSidebarOpen: false,
  selectedGroup: null,
  selectedItem: null,
  selectedBranch: initialBranch,
  isBranchModalOpen: false,
  appointmentUuid: "",
};

// Async thunk for fetching patient data
export const fetchPatientData = createAsyncThunk(
  "auth/fetchPatientData",
  async (caseId: string, { rejectWithValue }) => {
    try {
      const response = await getPatient(caseId);
      if (response?.data?.status === 200) {
        return {
          caseId,
          ...response?.data?.data,
          sex: {
            label: response?.data?.data?.sex,
            value: response?.data?.data?.sex,
          },
        };
      } else {
        return rejectWithValue("Failed to fetch patient data");
      }
    } catch (error: any) {
      return rejectWithValue(error?.message || "An unknown error occurred");
    }
  }
);

// Slice definition
const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    setClient: (state, action: PayloadAction<any>) => {
      state.profile = action.payload;
    },
    openBranchModal: (state) => {
      state.isBranchModalOpen = true;
    },
    closeBranchModal: (state) => {
      state.isBranchModalOpen = false;
    },
    setSelectedBranch: (state, action: PayloadAction<any>) => {
      state.selectedBranch = action.payload;
      state.isBranchModalOpen = false;
    },
    setPaginationModel: (state, action: PayloadAction<any>) => {
      state.paginationModel = action.payload;
    },
    clearPaginationModel: (state) => {
      state.paginationModel = initialState.paginationModel;
    },
    toggleSidebar: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },
    setSelectedGroup: (state, action: PayloadAction<string | null>) => {
      state.selectedGroup = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<string | null>) => {
      state.selectedItem = action.payload;
    },
    setAppointmentUuid: (state, action: PayloadAction<string>) => {
      state.appointmentUuid = action.payload;
    },
    clearAppointmentUuid: (state) => {
      state.appointmentUuid = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatientData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientData.fulfilled, (state, action) => {
        state.profile = action.payload;
        state.loading = false;
      })
      .addCase(fetchPatientData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

// Export actions and reducer
export const {
  setClient,
  setPaginationModel,
  clearPaginationModel,
  toggleSidebar,
  openBranchModal,
  closeBranchModal,
  setSelectedBranch,
  setSelectedGroup,
  setSelectedItem,
  setAppointmentUuid,
  clearAppointmentUuid,
} = commonSlice.actions;
export default commonSlice.reducer;
